import ArrowDownward from '@mui/icons-material/ArrowDownward';
import ArrowUpward from '@mui/icons-material/ArrowUpward';
import Sort from '@mui/icons-material/Sort';
import { buttonClasses, paperClasses, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import { useCallback, useMemo, useRef } from 'react';

import CheckableMenuItem from '~components/Menu/CheckableMenuItem';
import Menu, { MenuHandler } from '~components/Menu/Menu';
import {
  OverflowAwareText,
  SmallButton,
} from '~components/Order/ordersDispatchStyledComponents';
import {
  NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX,
  NEW_DISPATCH_TOPBAR_MENU_WIDTH_IN_PX,
} from '~constants/filters';

enum SortBy {
  ASC = 'asc',
  DESC = 'desc',
}

export type AvailableOrderOption<T extends string> = { value: T; label: string };
export type OrderOption<T extends string> = { orderBy: T; sortBy: `${SortBy}` };

interface OrderBySelectorProps<T extends string> {
  onChange: (order: OrderOption<T>) => void;
  order: OrderOption<T>;
  orderOptions: AvailableOrderOption<T>[];
}

export default function OrderBySelector<T extends string>({
  onChange,
  order,
  orderOptions,
}: OrderBySelectorProps<T>) {
  const theme = useTheme();
  const menuRef = useRef<MenuHandler>(null);

  const selectedOption = useMemo(() => {
    const option = orderOptions.find((option) => option.value === order.orderBy);

    if (!option) {
      throw new Error(`Option with value ${order.orderBy} not found`);
    }

    return option;
  }, [order.orderBy, orderOptions]);

  const handleChange = useCallback(
    (newOrderBy: T, newSortBy: `${SortBy}`) => {
      const sortByDidChange = newSortBy !== order.sortBy;
      const orderByDidChange = newOrderBy !== order.orderBy;

      if (sortByDidChange || orderByDidChange) {
        const updatedSortBy = sortByDidChange ? newSortBy : order.sortBy;
        const updatedOrderBy = orderByDidChange ? newOrderBy : order.orderBy;

        onChange({ orderBy: updatedOrderBy, sortBy: updatedSortBy });
        menuRef.current?.onClose?.();
      }
    },
    [onChange, order.sortBy, order.orderBy],
  );

  return (
    <Box
      sx={{
        display: 'flex',
        height: `${NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX}px`,
        overflow: 'hidden',
      }}
    >
      <SmallButton
        onClick={() => {
          handleChange(
            order.orderBy,
            order.sortBy === SortBy.ASC ? SortBy.DESC : SortBy.ASC,
          );
        }}
        size="small"
        startIcon={order.sortBy === SortBy.ASC ? <ArrowUpward /> : <ArrowDownward />}
        sx={{
          [`&.${buttonClasses.root}`]: {
            bgcolor: theme.semantic.surfaceRaised,
            color: theme.palette.text.primary,
            borderBottom: `solid 1px ${theme.brandV2.colors.treadGray7}`,
            borderBottomLeftRadius: '4px',
            borderBottomRightRadius: 0,
            borderLeft: `solid 1px ${theme.brandV2.colors.treadGray7}`,
            borderRight: 0,
            borderTop: `solid 1px ${theme.brandV2.colors.treadGray7}`,
            borderTopLeftRadius: '4px',
            borderTopRightRadius: 0,
            flex: 0,
            height: '100%',
            minWidth: `${NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX}px`,
            px: 0,
          },
          [`& .${buttonClasses.startIcon}`]: { p: 0, m: 0 },
        }}
      />

      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        <Menu
          sx={{
            [`& .${paperClasses.root}`]: {
              width: `${NEW_DISPATCH_TOPBAR_MENU_WIDTH_IN_PX}px`,
            },
          }}
          ref={menuRef}
          menuTrigger={
            <SmallButton
              size="small"
              startIcon={<Sort />}
              sx={{
                [`&.${buttonClasses.root}`]: {
                  bgcolor: theme.semantic.surfaceRaised,
                  color: theme.palette.text.primary,
                  border: `solid 1px ${theme.brandV2.colors.treadGray7}`,
                  borderBottomLeftRadius: 0,
                  borderBottomRightRadius: '4px',
                  borderTopLeftRadius: 0,
                  borderTopRightRadius: '4px',
                  height: '100%',
                  maxWidth: '100%',
                  overflow: 'hidden',
                },
              }}
            >
              <OverflowAwareText sx={{ fontWeight: 600 }}>
                {selectedOption.label}
              </OverflowAwareText>
            </SmallButton>
          }
        >
          {orderOptions.map((option) => (
            <CheckableMenuItem
              isChecked={option.value === selectedOption.value}
              key={option.value}
              label={option.label}
              onClick={() => handleChange(option.value, order.sortBy)}
            />
          ))}
        </Menu>
      </Box>
    </Box>
  );
}
